import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Config from "../Constants/Config";
import { useAuthDetails } from "../Common/cookiesHelper";
import { getManagerStoresCached } from "../reuseableComponents/managerStoresCache";
import { ciUrl } from "../Constants/nodeCi";

const readAllStore = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("AllStore"));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Stores a coupon / deal can be copied to: every store of the logged-in
// merchant except the current one.
// - merchant / employee: the AllStore list saved at login.
// - superadmin: AllStore holds the superadmin's own store list, not the
//   merchant being viewed, so fetch that merchant's stores from getManagerStores
//   (same request + session cache as Main.js and the report store filters).
export default function useCopyToStoreList() {
  const { LoginGetDashBoardRecordJson, userTypeData } = useAuthDetails();
  const merchantId = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const token = LoginGetDashBoardRecordJson?.token;
  const isSuperAdmin = userTypeData?.login_type === "superadmin";
  const [managerStores, setManagerStores] = useState([]);

  useEffect(() => {
    if (!isSuperAdmin || !merchantId || !token) return;

    let cancelled = false;
    const { token: _omitToken, ...body } = {
      name: "",
      merchant_id: merchantId,
      email: LoginGetDashBoardRecordJson?.data?.email,
      emp_id: localStorage.getItem("emp_id"),
      ...userTypeData,
    };

    getManagerStoresCached(merchantId, () =>
      axios.post(ciUrl(Config.GET_ALL_MANAGER_STORES), body, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
    )
      .then((response) => {
        if (cancelled) return;
        setManagerStores(
          response?.data?.status && Array.isArray(response.data.data)
            ? response.data.data
            : []
        );
      })
      .catch(() => {
        if (!cancelled) setManagerStores([]);
      });

    return () => {
      cancelled = true;
    };
    // merchantId + token identify the session; the rest is read in the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, merchantId, token]);

  return useMemo(() => {
    if (!isSuperAdmin) {
      return readAllStore().filter(
        (store) => store?.merchant_id !== merchantId
      );
    }
    // getManagerStores rows carry an internal `id`; key them by merchant_id
    // like the AllStore entries so chips and linked store matching line up.
    return managerStores
      .map((store) => ({
        ...store,
        id: String(store?.merchant_id ?? ""),
        name: store?.name || store?.store_name || "",
      }))
      .filter(
        (store) =>
          store.id && store.name && store.id !== String(merchantId ?? "")
      );
  }, [isSuperAdmin, managerStores, merchantId]);
}
